import { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import {
  BellRing, Plus, X, Save, Send, Users, Clock, AlertTriangle, History, Eye,
  CheckCircle2, AlertCircle,
} from 'lucide-react';

function EmailListInput({ label, items, onChange, placeholder, testId }) {
  const [draft, setDraft] = useState('');
  const add = () => {
    const v = draft.trim().toLowerCase();
    if (!v || items.includes(v)) { setDraft(''); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) { toast.error('Email inválido'); return; }
    onChange([...items, v]);
    setDraft('');
  };
  return (
    <div>
      <label className="block text-xs font-medium mb-1.5 flex items-center gap-1">
        <Users className="w-3.5 h-3.5" /> {label}
      </label>
      <div className="flex gap-1 mb-1.5">
        <Input type="email" value={draft} onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          placeholder={placeholder} className="text-sm h-8"
          data-testid={`${testId}-input`} />
        <Button size="sm" type="button" onClick={add} variant="outline" data-testid={`${testId}-add`}>
          <Plus className="w-4 h-4" />
        </Button>
      </div>
      <div className="flex flex-wrap gap-1.5 min-h-[28px]">
        {items.length === 0 && <span className="text-xs text-muted-foreground italic">Sin destinatarios.</span>}
        {items.map((e) => (
          <Badge key={e} variant="secondary" className="gap-1 pr-1" data-testid={`${testId}-chip`}>
            {e}
            <button type="button" onClick={() => onChange(items.filter((x) => x !== e))} className="hover:bg-red-200 rounded-full p-0.5">
              <X className="w-3 h-3" />
            </button>
          </Badge>
        ))}
      </div>
    </div>
  );
}

const fmtCLP = (n) => `$${Math.round(n || 0).toLocaleString('es-CL')}`;

export default function RecordatoriosFacRecepPage() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [log, setLog] = useState([]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [c, p, l] = await Promise.all([
        api.get('/recordatorios/facturas-pend-recepcion/config'),
        api.get('/recordatorios/facturas-pend-recepcion/preview'),
        api.get('/recordatorios/facturas-pend-recepcion/log?limit=10'),
      ]);
      setCfg(c.data);
      setPreviewData(p.data);
      setLog(l.data?.items || []);
    } catch (e) { toast.error('Error al cargar configuración'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/recordatorios/facturas-pend-recepcion/config', cfg);
      toast.success('Configuración guardada');
      await fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al guardar');
    } finally { setSaving(false); }
  };

  const sendNow = async () => {
    const all = [...(cfg?.to_5d || []), ...(cfg?.to_10d || [])];
    if (all.length === 0) { toast.error('Agregá al menos un destinatario antes de enviar'); return; }
    if (!window.confirm(`Vas a enviar ahora a ${[...new Set(all)].join(', ')}. ¿Continuar?`)) return;
    setSending(true);
    try {
      const res = await api.post('/recordatorios/facturas-pend-recepcion/send-now');
      const ok5 = res.data?.sends?.find((s) => s.nivel === '5d')?.success;
      const ok10 = res.data?.sends?.find((s) => s.nivel === '10d')?.success;
      toast.success(`Enviado · 5d:${ok5 ? '✓' : '—'} · 10d:${ok10 ? '✓' : '—'}`);
      await fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al enviar');
    } finally { setSending(false); }
  };

  if (loading || !cfg) {
    return <div className="space-y-3 p-3"><Skeleton className="h-12 w-1/2" /><Skeleton className="h-32" /><Skeleton className="h-48" /></div>;
  }

  const totalLive = (previewData?.count_5d || 0) + (previewData?.count_10d || 0);

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <BellRing className="w-6 h-6 text-amber-600" /> Recordatorios — Facturas Pend. Recepción
        </h1>
        <p className="text-sm text-muted-foreground">
          Email diario con facturas contabilizadas en AR sin recepción. Configurable por nivel (5d aviso · 10d urgente).
        </p>
      </div>

      {/* Preview */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Eye className="w-4 h-4" /> Vista previa (HOY)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-3">
            <div className="p-3 rounded-lg bg-amber-50 border border-amber-200">
              <p className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold">Aviso ≥ {cfg.dias_5_min}d</p>
              <p className="text-2xl font-bold text-amber-700">{previewData?.count_5d || 0}</p>
              <p className="text-[10px] text-amber-600">facturas</p>
            </div>
            <div className="p-3 rounded-lg bg-red-50 border border-red-200">
              <p className="text-[10px] uppercase tracking-wider text-red-700 font-semibold">Urgente ≥ {cfg.dias_10_min}d</p>
              <p className="text-2xl font-bold text-red-700">{previewData?.count_10d || 0}</p>
              <p className="text-[10px] text-red-600">facturas críticas</p>
            </div>
            <div className="p-3 rounded-lg bg-slate-50 border border-slate-200">
              <p className="text-[10px] uppercase tracking-wider text-slate-700 font-semibold">Total</p>
              <p className="text-2xl font-bold text-slate-700">{totalLive}</p>
              <p className="text-[10px] text-slate-600">en alerta</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Config */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Clock className="w-4 h-4" /> Configuración
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between bg-slate-50 rounded p-3 border">
            <div>
              <p className="text-sm font-semibold">Recordatorio diario habilitado</p>
              <p className="text-xs text-muted-foreground">Si está apagado, no se envía nada (y no se programa).</p>
            </div>
            <Switch checked={cfg.enabled} onCheckedChange={(v) => setCfg({ ...cfg, enabled: v })}
              data-testid="switch-enabled" />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs font-medium block mb-1">Hora envío</label>
              <Input type="time" value={cfg.hora || '08:00'}
                onChange={(e) => setCfg({ ...cfg, hora: e.target.value })}
                className="h-8 text-sm" data-testid="input-hora" />
            </div>
            <div>
              <label className="text-xs font-medium block mb-1">Aviso desde (días)</label>
              <Input type="number" min="1" max="60" value={cfg.dias_5_min}
                onChange={(e) => setCfg({ ...cfg, dias_5_min: Number(e.target.value) })}
                className="h-8 text-sm" data-testid="input-dias-5" />
            </div>
            <div>
              <label className="text-xs font-medium block mb-1">Urgente desde (días)</label>
              <Input type="number" min="2" max="90" value={cfg.dias_10_min}
                onChange={(e) => setCfg({ ...cfg, dias_10_min: Number(e.target.value) })}
                className="h-8 text-sm" data-testid="input-dias-10" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-3 p-3 rounded border border-amber-200 bg-amber-50/40">
              <p className="text-xs font-semibold text-amber-800 flex items-center gap-1">
                <AlertCircle className="w-3.5 h-3.5" /> Aviso (≥ {cfg.dias_5_min}d, &lt; {cfg.dias_10_min}d)
              </p>
              <EmailListInput label="Destinatarios (TO)" items={cfg.to_5d || []}
                onChange={(v) => setCfg({ ...cfg, to_5d: v })}
                placeholder="compras@..., bodega@..., corporativo@..."
                testId="to-5d" />
              <EmailListInput label="Copia (CC)" items={cfg.cc_5d || []}
                onChange={(v) => setCfg({ ...cfg, cc_5d: v })}
                placeholder="gerencia@..."
                testId="cc-5d" />
            </div>
            <div className="space-y-3 p-3 rounded border border-red-200 bg-red-50/40">
              <p className="text-xs font-semibold text-red-800 flex items-center gap-1">
                <AlertTriangle className="w-3.5 h-3.5" /> Urgente (≥ {cfg.dias_10_min}d)
              </p>
              <EmailListInput label="Destinatarios (TO)" items={cfg.to_10d || []}
                onChange={(v) => setCfg({ ...cfg, to_10d: v })}
                placeholder="compras@..., corporativo@..., gerencia@..."
                testId="to-10d" />
              <EmailListInput label="Copia (CC)" items={cfg.cc_10d || []}
                onChange={(v) => setCfg({ ...cfg, cc_10d: v })}
                placeholder="opcional"
                testId="cc-10d" />
            </div>
          </div>

          <div className="flex items-center gap-2 justify-end">
            <Button variant="outline" onClick={sendNow} disabled={sending || !cfg.enabled}
              className="text-amber-700 border-amber-300 hover:bg-amber-50"
              data-testid="btn-send-now">
              <Send className="w-4 h-4 mr-1" /> {sending ? 'Enviando…' : 'Enviar ahora'}
            </Button>
            <Button onClick={save} disabled={saving} data-testid="btn-save">
              <Save className="w-4 h-4 mr-1" /> {saving ? 'Guardando…' : 'Guardar'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* History */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <History className="w-4 h-4" /> Historial reciente
          </CardTitle>
        </CardHeader>
        <CardContent>
          {log.length === 0 ? (
            <p className="text-xs text-muted-foreground italic">Sin ejecuciones aún.</p>
          ) : (
            <div className="space-y-1 text-xs">
              {log.map((e, i) => (
                <div key={i} className="flex items-center gap-2 p-2 rounded hover:bg-slate-50 border">
                  <span className="text-[10px] text-muted-foreground font-mono w-44">{e.timestamp?.slice(0, 19).replace('T', ' ')}</span>
                  <span className="text-[10px]">{e.manual ? '🖱 Manual' : '⏰ Auto'}</span>
                  <span className="text-amber-700">5d: {e.atrasadas_5d_count}</span>
                  <span className="text-red-700">10d: {e.atrasadas_10d_count}</span>
                  <div className="ml-auto flex gap-1">
                    {(e.sends || []).map((s, j) => (
                      <span key={j} className={`text-[10px] px-1.5 py-0.5 rounded ${
                        s.success ? 'bg-emerald-100 text-emerald-700' :
                        s.skipped ? 'bg-slate-100 text-slate-600' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {s.nivel}: {s.success ? '✓' : s.skipped || 'error'}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <p className="text-[11px] text-muted-foreground flex items-center gap-1.5">
        <CheckCircle2 className="w-3.5 h-3.5" />
        Tip: configurá distintos destinatarios por nivel. 5d para que compras y bodega tomen acción; 10d para que la gerencia tenga visibilidad.
      </p>
    </div>
  );
}
